import React, { useState, useEffect } from 'react';
import { Menu, X } from 'lucide-react';
import { Button } from './ui/button';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { LanguageSwitcher } from './LanguageSwitcher';

export const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
    // handleScroll is defined inside effect, setIsScrolled is stable
  }, []);

  const scrollToSection = (sectionId) => {
    if (location.pathname !== '/') {
      navigate('/');
      setTimeout(() => {
        const element = document.getElementById(sectionId);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      }, 100);
    } else {
      const element = document.getElementById(sectionId);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    }
    setIsMobileMenuOpen(false);
  };

  const goToHome = () => {
    navigate('/');
    window.scrollTo({ top: 0, behavior: 'smooth' });
    setIsMobileMenuOpen(false);
  };

  return (
    <header 
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? 'bg-white/95 backdrop-blur-md shadow-lg' : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-28 sm:h-36">
          {/* Logo */}
          <div className="flex-shrink-0 cursor-pointer" onClick={goToHome}>
            <img 
              src="https://customer-assets.emergentagent.com/job_e4fe7836-aa82-4d8f-9622-45c6138206f4/artifacts/912191pk_Logo%20para%20inmobiliaria%20moderno%20azul%20y%20dorado.png" 
              alt="Comfort Home" 
              className="h-28 sm:h-36 w-auto transition-transform duration-300 hover:scale-105"
            />
          </div>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center space-x-1">
            <button 
              onClick={goToHome}
              className={`px-4 py-2 text-sm font-medium transition-colors ${
                isScrolled ? 'text-gray-700 hover:text-[#C5A35C]' : 'text-white hover:text-[#C5A35C]'
              }`}
            >
              {t('nav.home')}
            </button>
            <button 
              onClick={() => scrollToSection('benefits')}
              className={`px-4 py-2 text-sm font-medium transition-colors ${
                isScrolled ? 'text-gray-700 hover:text-[#C5A35C]' : 'text-white hover:text-[#C5A35C]'
              }`}
            >
              {t('nav.benefits')}
            </button>
            <button 
              onClick={() => scrollToSection('solutions')}
              className={`px-4 py-2 text-sm font-medium transition-colors ${
                isScrolled ? 'text-gray-700 hover:text-[#C5A35C]' : 'text-white hover:text-[#C5A35C]'
              }`}
            >
              {t('nav.solutions')}
            </button>
            
            {/* Language Switcher */}
            <LanguageSwitcher />
            
            <Button 
              onClick={() => scrollToSection('contact')}
              className="ml-4 bg-[#C5A35C] hover:bg-[#B8933D] text-white shadow-lg"
            >
              {t('nav.contact')}
            </Button>
          </nav>

          {/* Mobile menu button */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className={`lg:hidden p-2 rounded-md transition-colors ${
              isScrolled 
                ? 'text-gray-700 hover:text-[#C5A35C] hover:bg-gray-100' 
                : 'text-white hover:text-[#C5A35C] hover:bg-white/10'
            }`}
          >
            {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      {/* Mobile Navigation */}
      {isMobileMenuOpen && (
        <div className="lg:hidden bg-white border-t border-gray-200 shadow-lg">
          <div className="px-4 py-6 space-y-3">
            <button 
              onClick={goToHome}
              className="block w-full text-left px-4 py-3 text-base font-medium text-gray-700 hover:text-[#C5A35C] hover:bg-gray-50 rounded-lg transition-colors"
            >
              {t('nav.home')}
            </button>
            <button 
              onClick={() => scrollToSection('benefits')}
              className="block w-full text-left px-4 py-3 text-base font-medium text-gray-700 hover:text-[#C5A35C] hover:bg-gray-50 rounded-lg transition-colors"
            >
              {t('nav.benefits')}
            </button>
            <button 
              onClick={() => scrollToSection('solutions')}
              className="block w-full text-left px-4 py-3 text-base font-medium text-gray-700 hover:text-[#C5A35C] hover:bg-gray-50 rounded-lg transition-colors"
            >
              {t('nav.solutions')}
            </button>
            
            {/* Language Switcher Mobile */}
            <div className="pt-2">
              <LanguageSwitcher />
            </div>
            
            <Button 
              onClick={() => scrollToSection('contact')}
              className="w-full bg-[#C5A35C] hover:bg-[#B8933D] text-white mt-4"
            >
              {t('nav.contact')}
            </Button>
          </div>
        </div>
      )}
    </header>
  );
};
